/**
 * This file exposes frequently used date methods.
 *
 * These methods are auto-imported in components and pages and can be used
 * directly in templates, <script setup> or anywhere in the Options API without
 * having to manually import them.
 */
import dayjs, { type Dayjs, type PluginFunc } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import localeData from 'dayjs/plugin/localeData'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isBetween from 'dayjs/plugin/isBetween'
import weekday from 'dayjs/plugin/weekday'
import type { Nullable } from '~/types'
import { isMetaServer } from '~/utils/meta'

import 'dayjs/locale/en'
import 'dayjs/locale/fr'

let _clientInstance: Nullable<ReturnType<typeof createDatesInstance>> = null

/**
 * This function fixes missing formatters for localizesFormat.
 *
 * Oddly, the formatters work when dayjs is used as a singleton, but not as a
 * plugin, and it's only English that's not working. There's a bug report about
 * this as well which is still open: https://github.com/iamkun/dayjs/issues/2281
 *
 * @param _option The option to fix the missing formatters for
 * @param dayjsClass The dayjs class to fix the missing formatters for
 */
const fixMissingLocalizedFormatters: PluginFunc = (_option, dayjsClass) => {
  const proto = dayjsClass.prototype
  const oldFormat = proto.format

  proto.format = function (formatStr: string) {
    const customFormats = {
      l: 'M/D/YYYY',
      ll: 'MMM D, YYYY',
      lll: 'MMM D, YYYY h:mm A',
      llll: 'ddd, MMM D, YYYY h:mm A',
    }

    // If it's a custom format and the local is `en`, replace it.
    if (customFormats[formatStr as keyof typeof customFormats] && this.locale() === 'en') {
      formatStr = customFormats[formatStr as keyof typeof customFormats]
    }

    return oldFormat.call(this, formatStr)
  }
}

/**
 * Factory function that creates a new dayjs instance.
 */
function createDatesInstance() {
  const dayjsInstance = dayjs

  dayjsInstance.extend(utc)
  dayjsInstance.extend(localeData)
  dayjsInstance.extend(timezone)
  dayjsInstance.extend(customParseFormat)
  dayjsInstance.extend(localizedFormat)
  dayjsInstance.extend(advancedFormat)
  dayjsInstance.extend(isSameOrBefore)
  dayjsInstance.extend(isSameOrAfter)
  dayjsInstance.extend(isBetween)
  dayjsInstance.extend(weekday)
  dayjsInstance.extend(fixMissingLocalizedFormatters)

  return dayjsInstance
}

/**
 * Returns the configured dayjs instance (unique per request on the server,
 * shared on the client).
 */
export function getDatetime() {
  if (isMetaServer) {
    return createDatesInstance()
  }

  if (!_clientInstance) {
    _clientInstance = createDatesInstance()
  }

  return _clientInstance
}

// #region exports
export type DatesInstance = Dayjs
export type DatesParam = Nullable<number | string | Date | DatesInstance>

/** Configured dayjs at module load (client singleton). Auto-imported as `datetime`. */
export default getDatetime()
// #endregion
